/* ==========================================================================
   Admin Portal Settings Page
   Task F22: Portal configuration page for pharmacy admins
   ========================================================================== */

import { useState, useEffect, useRef, useCallback } from 'react'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { FormField } from '@/components/ui/FormField'
import { Alert } from '@/components/ui/Alert'
import { AdminSettingsSkeleton } from '@/components/skeletons'
import { FileUploadField } from '@/components/intake/FileUploadField'
import { AddressFields } from '@/components/forms/AddressFields'
import { PharmacyNotesEditor } from '@/components/admin/PharmacyNotesEditor'
import { useAdminAuth } from '@/hooks/useAdminAuth'
import { useToast } from '@/context/ToastContext'
import { getPharmacy, updatePharmacy, uploadPharmacyLogo, adminClient } from '@/api/admin'
import { ApiClientError } from '@/api/client'
import type { PharmacyResponse, PharmacyUpdate, AddressSchema, PortalType } from '@/api/types'
import {
  DEFAULT_COUNTRY,
  SUPPORTED_PHARMACY_COUNTRIES,
  isCanada,
  postalLabel,
  regionLabel,
  validatePostalCode,
} from '@/utils/countries'

/* --------------------------------------------------------------------------
   Phone Formatting
   -------------------------------------------------------------------------- */

/**
 * Format phone number as user types: (XXX) XXX-XXXX
 */
function formatPhoneNumber(value: string): string {
  const digits = value.replace(/\D/g, '')
  const trimmed = digits.slice(0, 10)

  if (trimmed.length === 0) return ''
  if (trimmed.length <= 3) return `(${trimmed}`
  if (trimmed.length <= 6) return `(${trimmed.slice(0, 3)}) ${trimmed.slice(3)}`
  return `(${trimmed.slice(0, 3)}) ${trimmed.slice(3, 6)}-${trimmed.slice(6)}`
}

/**
 * Strip formatting from phone number for storage.
 */
function stripPhoneFormatting(value: string): string {
  return value.replace(/\D/g, '')
}

/* --------------------------------------------------------------------------
   Validation
   -------------------------------------------------------------------------- */

interface FormErrors {
  phone?: string
  street?: string
  city?: string
  state?: string
  zip?: string
  npi?: string
  state_license?: string
  primary_color?: string
  secondary_color?: string
}

interface FormValues {
  phone: string
  country: string
  street: string
  city: string
  state: string | null
  zip: string
  npi: string
  state_license: string
  primary_color: string
  secondary_color: string
}

function validateForm(values: FormValues): FormErrors {
  const errors: FormErrors = {}

  // Phone validation (required)
  const phoneDigits = stripPhoneFormatting(values.phone)
  if (!phoneDigits) {
    errors.phone = 'Phone number is required'
  } else if (phoneDigits.length !== 10) {
    errors.phone = 'Phone number must be 10 digits'
  }

  // Address validation (all required if any are filled)
  const hasAnyAddress = values.street || values.city || values.state || values.zip
  if (hasAnyAddress) {
    if (!values.street.trim()) {
      errors.street = 'Street address is required'
    }
    if (!values.city.trim()) {
      errors.city = 'City is required'
    }
    if (!values.state) {
      errors.state = `${regionLabel(values.country)} is required`
    }
    if (!values.zip.trim()) {
      errors.zip = `${postalLabel(values.country)} is required`
    } else {
      const postalError = validatePostalCode(values.country, values.zip)
      if (postalError) errors.zip = postalError
    }
  }

  // NPI validation (optional but if provided must be 10 digits)
  if (values.npi && !/^\d{10}$/.test(values.npi.replace(/\D/g, ''))) {
    errors.npi = 'NPI must be exactly 10 digits'
  }

  // Color validation (optional but must be valid hex)
  if (values.primary_color && !/^#[0-9A-Fa-f]{6}$/.test(values.primary_color)) {
    errors.primary_color = 'Must be a valid hex color (e.g., #5C288F)'
  }
  if (values.secondary_color && !/^#[0-9A-Fa-f]{6}$/.test(values.secondary_color)) {
    errors.secondary_color = 'Must be a valid hex color (e.g., #BBA2FE)'
  }

  return errors
}

/* --------------------------------------------------------------------------
   Component
   -------------------------------------------------------------------------- */

export function Settings() {
  const { pharmacy: authPharmacy, refreshPharmacy } = useAdminAuth()
  const { success: showSuccess, error: showError } = useToast()

  // Form state
  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [faxNumber, setFaxNumber] = useState('')
  const [street, setStreet] = useState('')
  const [city, setCity] = useState('')
  const [state, setState] = useState<string | null>(null)
  const [zip, setZip] = useState('')
  const [npi, setNpi] = useState('')
  const [stateLicense, setStateLicense] = useState('')
  const [logoUrl, setLogoUrl] = useState<string | null>(null)
  const [logoFileName, setLogoFileName] = useState<string | null>(null)
  const [logoChanged, setLogoChanged] = useState(false)
  const [primaryColor, setPrimaryColor] = useState('')
  const [secondaryColor, setSecondaryColor] = useState('')
  const [landingPageEnabled, setLandingPageEnabled] = useState(true)
  const [showCatalogOnLanding, setShowCatalogOnLanding] = useState(true)
  const [showInviteFormOnLanding, setShowInviteFormOnLanding] = useState(true)
  const [portalType, setPortalType] = useState<PortalType>('human')
  const [signatureRequired, setSignatureRequired] = useState(false)
  const [patientAddressRequired, setPatientAddressRequired] = useState(false)
  const [phoneRequired, setPhoneRequired] = useState(false)
  const [providerAddressLock, setProviderAddressLock] = useState(false)
  const [allowCountryField, setAllowCountryField] = useState(false)
  // Pharmacy country (PORTAL-161): drives address labels/validation here and
  // localizes the whole portal for Canadian pharmacies.
  const [country, setCountry] = useState<string>(DEFAULT_COUNTRY)
  const [hidePaymentDeliverySection, setHidePaymentDeliverySection] = useState(false)
  const [hideDiagnosisCode, setHideDiagnosisCode] = useState(false)
  const [hidePaymentType, setHidePaymentType] = useState(false)
  const [hideDeliveryMethod, setHideDeliveryMethod] = useState(false)
  // PORTAL-202: per-field requirements. A requirement has no effect while its
  // field is hidden, but the checkbox still shows and edits the stored value: a
  // provider whose override un-hides the field inherits it.
  const [diagnosisCodeRequired, setDiagnosisCodeRequired] = useState(false)
  const [paymentTypeRequired, setPaymentTypeRequired] = useState(false)
  const [deliveryMethodRequired, setDeliveryMethodRequired] = useState(false)
  const [hideMachineReadableZone, setHideMachineReadableZone] = useState(false)
  const [pharmacyNotes, setPharmacyNotes] = useState('')
  // PORTAL-159: the note as last loaded or saved. Like logoChanged, the note is
  // only sent when it differs from this baseline, so an unrelated save from a
  // Settings tab opened earlier never overwrites (or clears) a note another
  // admin published in the meantime. Comparing against the baseline rather
  // than flagging the first keystroke means an edit the admin backs out by
  // hand counts as unchanged too.
  const [savedPharmacyNotes, setSavedPharmacyNotes] = useState('')
  const notesChanged = pharmacyNotes !== savedPharmacyNotes
  const [addressVerified, setAddressVerified] = useState<boolean | null>(null)

  // PORTAL-65 rollup: the Payment & Delivery section is fully hidden when the
  // parent flag is set OR all three children are hidden. This single derived
  // value drives both the master toggle's appearance AND child locking, so
  // hiding all three elements individually behaves exactly like flipping the
  // parent — the children lock, and the only way to edit them again is to turn
  // the master OFF (which resets all three to visible). The backend mirrors
  // this with an "all children ON -> parent ON" invariant, so the stored row
  // converges to parent ON + all children ON whenever the section is hidden.
  const sectionFullyHidden =
    hidePaymentDeliverySection ||
    (hideDiagnosisCode && hidePaymentType && hideDeliveryMethod)
  const diagnosisCodeHidden = sectionFullyHidden || hideDiagnosisCode
  const paymentTypeHidden = sectionFullyHidden || hidePaymentType
  const deliveryMethodHidden = sectionFullyHidden || hideDeliveryMethod

  // UI state
  const [errors, setErrors] = useState<FormErrors>({})
  const [touched, setTouched] = useState<Record<string, boolean>>({})
  const [loading, setLoading] = useState(false)
  const [initialLoading, setInitialLoading] = useState(true)
  const [apiError, setApiError] = useState<string | undefined>()
  const [copySuccess, setCopySuccess] = useState(false)

  // Refs for color inputs
  const primaryColorInputRef = useRef<HTMLInputElement>(null)
  const secondaryColorInputRef = useRef<HTMLInputElement>(null)

  // Get the portal URL
  const portalUrl = authPharmacy
    ? `${window.location.origin}/rx/${authPharmacy.portal_slug}`
    : ''

  // Load pharmacy data on mount
  useEffect(() => {
    async function loadPharmacy() {
      if (!authPharmacy?.portal_slug) return

      try {
        setInitialLoading(true)
        const pharmacy = await getPharmacy(authPharmacy.portal_slug)
        populateForm(pharmacy)
      } catch (err) {
        if (err instanceof ApiClientError) {
          showError(err.getUserMessage())
        } else {
          showError('Failed to load pharmacy settings')
        }
      } finally {
        setInitialLoading(false)
      }
    }

    loadPharmacy()
  }, [authPharmacy?.portal_slug, showError])

  // Populate form with pharmacy data
  function populateForm(pharmacy: PharmacyResponse) {
    setName(pharmacy.name || '')
    setPhone(pharmacy.phone ? formatPhoneNumber(pharmacy.phone) : '')
    setFaxNumber(pharmacy.fax_number ? formatPhoneNumber(pharmacy.fax_number) : '')
    setStreet(pharmacy.address?.street || '')
    setCity(pharmacy.address?.city || '')
    setState(pharmacy.address?.state || null)
    setZip(pharmacy.address?.zip || '')
    setNpi(pharmacy.npi || '')
    setStateLicense(pharmacy.state_license || '')
    setLogoUrl(pharmacy.logo_url || null)
    setLogoFileName(
      pharmacy.logo_url
        ? (pharmacy.logo_url.split('/').pop()?.split('?')[0] || 'logo')
        : null,
    )
    setLogoChanged(false)
    setPrimaryColor(pharmacy.primary_color || '')
    setSecondaryColor(pharmacy.secondary_color || '')
    setLandingPageEnabled(pharmacy.landing_page_enabled ?? true)
    setShowCatalogOnLanding(pharmacy.show_catalog_on_landing_page ?? true)
    setShowInviteFormOnLanding(pharmacy.show_invite_form_on_landing_page ?? true)
    setPortalType(pharmacy.portal_type ?? 'human')
    setSignatureRequired(pharmacy.signature_required ?? false)
    setPatientAddressRequired(pharmacy.patient_address_required ?? false)
    setPhoneRequired(pharmacy.phone_required ?? false)
    setProviderAddressLock(pharmacy.provider_address_lock ?? false)
    setAllowCountryField(pharmacy.allow_country_field ?? false)
    setCountry(pharmacy.country ?? DEFAULT_COUNTRY)
    setHidePaymentDeliverySection(pharmacy.hide_payment_delivery_section ?? false)
    setHideDiagnosisCode(pharmacy.hide_diagnosis_code ?? false)
    setHidePaymentType(pharmacy.hide_payment_type ?? false)
    setHideDeliveryMethod(pharmacy.hide_delivery_method ?? false)
    setDiagnosisCodeRequired(pharmacy.diagnosis_code_required ?? false)
    setPaymentTypeRequired(pharmacy.payment_type_required ?? false)
    setDeliveryMethodRequired(pharmacy.delivery_method_required ?? false)
    setHideMachineReadableZone(pharmacy.hide_machine_readable_zone ?? false)
    setPharmacyNotes(pharmacy.pharmacy_notes ?? '')
    setSavedPharmacyNotes(pharmacy.pharmacy_notes ?? '')
  }

  // Get current form values
  function getFormValues(): FormValues {
    return {
      phone,
      country,
      street,
      city,
      state,
      zip,
      npi,
      state_license: stateLicense,
      primary_color: primaryColor,
      secondary_color: secondaryColor,
    }
  }

  // Handle field blur
  function handleBlur(field: string) {
    setTouched((prev) => ({ ...prev, [field]: true }))
    const fieldErrors = validateForm(getFormValues())
    setErrors((prev) => ({ ...prev, [field]: fieldErrors[field as keyof FormErrors] }))
  }

  // Handle phone change with formatting
  function handlePhoneChange(value: string) {
    setPhone(formatPhoneNumber(value))
    setApiError(undefined)
    if (touched.phone) {
      const phoneDigits = stripPhoneFormatting(value)
      if (!phoneDigits) {
        setErrors((prev) => ({ ...prev, phone: 'Phone number is required' }))
      } else if (phoneDigits.length !== 10) {
        setErrors((prev) => ({ ...prev, phone: 'Phone number must be 10 digits' }))
      } else {
        setErrors((prev) => ({ ...prev, phone: undefined }))
      }
    }
  }


  // Handle form submission
  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()

    const values = getFormValues()
    const formErrors = validateForm(values)
    setErrors(formErrors)
    setTouched({
      phone: true,
      street: true,
      city: true,
      state: true,
      zip: true,
      npi: true,
      state_license: true,
      primary_color: true,
      secondary_color: true,
    })

    const hasErrors = Object.values(formErrors).some(Boolean)
    if (hasErrors) {
      return
    }

    if (!authPharmacy?.portal_slug) {
      showError('Pharmacy not found')
      return
    }

    setLoading(true)
    setApiError(undefined)

    try {
      // Build address object if any address fields are filled
      let address: AddressSchema | null = null
      if (street || city || state || zip) {
        address = {
          street: street.trim(),
          city: city.trim(),
          state: state || '',
          zip: zip.trim(),
          country,
        }
      }

      const updateData: PharmacyUpdate = {
        phone: stripPhoneFormatting(phone),
        country,
        address,
        address_verified: address ? (addressVerified === true) : undefined,
        // NPI is a US identifier; Canadian pharmacies do not carry one (PORTAL-161)
        npi: isCanada(country) ? null : npi.replace(/\D/g, '') || null,
        state_license: stateLicense.trim() || null,
        primary_color: primaryColor || null,
        secondary_color: secondaryColor || null,
        portal_type: portalType,
        // Only send the note when this admin edited it, so an unrelated save
        // never overwrites a note published from another session. Sent as ""
        // when cleared; the backend stores a blank note as null.
        ...(notesChanged ? { pharmacy_notes: pharmacyNotes } : {}),
        // Only send logo_url when it changed (new upload or removal)
        // to avoid sending back the pre-signed URL
        ...(logoChanged ? { logo_url: logoUrl || null } : {}),
      }

      await updatePharmacy(authPharmacy.portal_slug, updateData)
      setSavedPharmacyNotes(pharmacyNotes)
      await refreshPharmacy()

      showSuccess('Settings saved successfully!')
    } catch (err) {
      if (err instanceof ApiClientError) {
        setApiError(err.getUserMessage())
        showError(err.getUserMessage())
      } else {
        const message = 'An unexpected error occurred. Please try again.'
        setApiError(message)
        showError(message)
      }
    } finally {
      setLoading(false)
    }
  }

  // Copy portal URL to clipboard
  async function copyPortalUrl() {
    try {
      await navigator.clipboard.writeText(portalUrl)
      setCopySuccess(true)
      showSuccess('Portal URL copied to clipboard!')
      setTimeout(() => setCopySuccess(false), 2000)
    } catch {
      showError('Failed to copy URL')
    }
  }

  // Logo upload handler
  const handleLogoUpload = useCallback(
    async (file: File) => {
      if (!authPharmacy?.portal_slug) throw new Error('Pharmacy not found')
      return uploadPharmacyLogo(authPharmacy.portal_slug, file)
    },
    [authPharmacy?.portal_slug],
  )

  // Check if form has any validation errors
  const hasValidationErrors = Object.values(errors).some(Boolean)

  // PORTAL-202: the "Required to submit" checkbox under each Payment & Delivery
  // hide toggle. It stays editable while its field is hidden: the requirement
  // has no effect then, but a provider whose override shows the field inherits it.
  const renderRequiredCheckbox = (
    field: keyof Pick<
      PharmacyUpdate,
      'diagnosis_code_required' | 'payment_type_required' | 'delivery_method_required'
    >,
    fieldName: string,
    checked: boolean,
    setChecked: (value: boolean) => void,
    fieldHidden: boolean,
  ) => (
    <label className="mt-2 inline-flex items-center gap-2 cursor-pointer">
      <input
        type="checkbox"
        checked={checked}
        disabled={loading}
        onChange={async (e) => {
          if (!authPharmacy?.portal_slug) return
          const newValue = e.target.checked
          setChecked(newValue)
          const update: PharmacyUpdate = {}
          update[field] = newValue
          try {
            await updatePharmacy(authPharmacy.portal_slug, update)
            await refreshPharmacy()
            showSuccess(`${fieldName} requirement ${newValue ? 'enabled' : 'disabled'}`)
          } catch (err) {
            setChecked(!newValue) // revert
            if (err instanceof ApiClientError) {
              showError(err.getUserMessage())
            } else {
              showError('Failed to update setting')
            }
          }
        }}
        className="w-4 h-4 rounded border-gray-300 text-deep-purple focus:ring-lavender"
      />
      <span className="text-xs text-gray-700">
        Required to submit
        {fieldHidden && (
          <span
            className="text-gray-500"
            title="Applies only to providers whose override shows this field."
          >
            {' '}(no effect while hidden)
          </span>
        )}
      </span>
    </label>
  )

  // Show loading skeleton while initial data loads
  if (initialLoading) {
    return (
      <div className="p-6 sm:p-8">
        <div className="max-w-4xl">
          <AdminSettingsSkeleton />
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 sm:p-8">
      <div className="max-w-4xl">
        {/* Page Header */}
        <h1 className="font-serif text-2xl sm:text-3xl text-charcoal mb-6">Portal Settings</h1>

        {/* API error alert */}
        {apiError && (
          <Alert variant="error" className="mb-6">
            {apiError}
          </Alert>
        )}

        <form onSubmit={handleSubmit} noValidate>
          <div className="space-y-6">
            {/* Pharmacy Information Section */}
            <Card>
              <h2 className="font-serif text-xl text-charcoal mb-4">
                Pharmacy Information
              </h2>

              <div className="space-y-4">
                {/* Pharmacy Name (read-only) */}
                <FormField
                  label="Pharmacy Name"
                  helperText="Contact support to change your pharmacy name"
                  htmlFor="name"
                >
                  <Input
                    id="name"
                    type="text"
                    value={name}
                    disabled
                    className="bg-gray-50"
                  />
                </FormField>

                {/* Phone and Fax */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FormField
                    label="Phone"
                    required
                    error={touched.phone ? errors.phone : undefined}
                    htmlFor="phone"
                  >
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="(555) 555-5555"
                      value={phone}
                      onChange={(e) => handlePhoneChange(e.target.value)}
                      onBlur={() => handleBlur('phone')}
                      error={touched.phone && !!errors.phone}
                      disabled={loading}
                    />
                  </FormField>

                  {/* Fax (read-only) */}
                  <FormField
                    label="Fax"
                    helperText="Contact support to change your fax number"
                    htmlFor="fax_number"
                  >
                    <Input
                      id="fax_number"
                      type="tel"
                      value={faxNumber}
                      disabled
                      className="bg-gray-50"
                    />
                  </FormField>
                </div>

                {/* Country (PORTAL-161) — Canada switches the portal to
                    Province / Postal Code labels and removes NPI / DEA. */}
                <FormField
                  label="Country"
                  helperText="Canadian pharmacies use Province and Postal Code fields and do not collect NPI or DEA."
                  htmlFor="pharmacy-country"
                >
                  <Select
                    options={SUPPORTED_PHARMACY_COUNTRIES}
                    value={country}
                    onChange={(v) => {
                      const next = v || DEFAULT_COUNTRY
                      if (next === country) return
                      setCountry(next)
                      // Region lists and postal formats differ — reset them.
                      setState(null)
                      setZip('')
                      setAddressVerified(null)
                      setApiError(undefined)
                    }}
                    disabled={loading}
                  />
                </FormField>

                {/* Address with autocomplete and verification */}
                <AddressFields
                  country={country}
                  street={street}
                  city={city}
                  state={state}
                  zip={zip}
                  onStreetChange={(v) => { setStreet(v); setApiError(undefined) }}
                  onCityChange={(v) => { setCity(v); setApiError(undefined) }}
                  onStateChange={(v) => { setState(v); setApiError(undefined) }}
                  onZipChange={(v) => { setZip(v); setApiError(undefined) }}
                  verified={addressVerified}
                  onVerified={setAddressVerified}
                  errors={errors}
                  touched={touched}
                  onBlur={handleBlur}
                  disabled={loading}
                  apiClient={adminClient}
                  heading="Address"
                />

                {/* NPI (US only) and State License */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {!isCanada(country) && (
                  <FormField
                    label="NPI"
                    error={touched.npi ? errors.npi : undefined}
                    helperText="10-digit National Provider Identifier"
                    htmlFor="npi"
                  >
                    <Input
                      id="npi"
                      type="text"
                      inputMode="numeric"
                      placeholder="1234567890"
                      value={npi}
                      onChange={(e) => {
                        const digits = e.target.value.replace(/\D/g, '').slice(0, 10)
                        setNpi(digits)
                        setApiError(undefined)
                      }}
                      onBlur={() => handleBlur('npi')}
                      error={touched.npi && !!errors.npi}
                      disabled={loading}
                      maxLength={10}
                    />
                  </FormField>
                  )}

                  <FormField
                    label={isCanada(country) ? 'Provincial License' : 'State License'}
                    error={touched.state_license ? errors.state_license : undefined}
                    htmlFor="state_license"
                  >
                    <Input
                      id="state_license"
                      type="text"
                      placeholder="License number"
                      value={stateLicense}
                      onChange={(e) => {
                        setStateLicense(e.target.value)
                        setApiError(undefined)
                      }}
                      onBlur={() => handleBlur('state_license')}
                      error={touched.state_license && !!errors.state_license}
                      disabled={loading}
                    />
                  </FormField>
                </div>
              </div>
            </Card>

            {/* Branding Section */}
            <Card>
              <h2 className="font-serif text-xl text-charcoal mb-4">Branding</h2>

              <div className="space-y-4">
                {/* Logo Upload */}
                <FileUploadField
                  label="Pharmacy Logo"
                  accept="image/png,image/jpeg,image/gif,image/webp"
                  maxSizeMB={5}
                  value={logoUrl}
                  fileName={logoFileName}
                  onChange={(url, name) => {
                    setLogoUrl(url)
                    setLogoFileName(name)
                    setLogoChanged(true)
                    setApiError(undefined)
                  }}
                  onUpload={handleLogoUpload}
                  helperText="PNG, JPG, GIF, or WebP. Used in your portal header."
                />

                {/* Color Pickers */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FormField
                    label="Primary Color"
                    error={touched.primary_color ? errors.primary_color : undefined}
                    helperText="Main brand color for buttons and headers"
                    htmlFor="primary_color"
                  >
                    <div className="flex gap-2">
                      <div className="relative">
                        <input
                          ref={primaryColorInputRef}
                          type="color"
                          value={primaryColor || '#5C288F'}
                          onChange={(e) => {
                            setPrimaryColor(e.target.value)
                            setApiError(undefined)
                          }}
                          className="sr-only"
                          id="primary_color_picker"
                        />
                        <button
                          type="button"
                          onClick={() => primaryColorInputRef.current?.click()}
                          className="w-10 h-10 rounded-lg border-2 border-gray-300 cursor-pointer hover:border-gray-400 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-lavender focus-visible:ring-offset-2"
                          style={{ backgroundColor: primaryColor || '#5C288F' }}
                          aria-label="Choose primary color"
                          disabled={loading}
                        />
                      </div>
                      <Input
                        id="primary_color"
                        type="text"
                        placeholder="#5C288F"
                        value={primaryColor}
                        onChange={(e) => {
                          setPrimaryColor(e.target.value)
                          setApiError(undefined)
                        }}
                        onBlur={() => handleBlur('primary_color')}
                        error={touched.primary_color && !!errors.primary_color}
                        disabled={loading}
                        className="flex-1"
                      />
                    </div>
                  </FormField>

                  <FormField
                    label="Secondary Color"
                    error={touched.secondary_color ? errors.secondary_color : undefined}
                    helperText="Accent color for highlights and focus states"
                    htmlFor="secondary_color"
                  >
                    <div className="flex gap-2">
                      <div className="relative">
                        <input
                          ref={secondaryColorInputRef}
                          type="color"
                          value={secondaryColor || '#BBA2FE'}
                          onChange={(e) => {
                            setSecondaryColor(e.target.value)
                            setApiError(undefined)
                          }}
                          className="sr-only"
                          id="secondary_color_picker"
                        />
                        <button
                          type="button"
                          onClick={() => secondaryColorInputRef.current?.click()}
                          className="w-10 h-10 rounded-lg border-2 border-gray-300 cursor-pointer hover:border-gray-400 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-lavender focus-visible:ring-offset-2"
                          style={{ backgroundColor: secondaryColor || '#BBA2FE' }}
                          aria-label="Choose secondary color"
                          disabled={loading}
                        />
                      </div>
                      <Input
                        id="secondary_color"
                        type="text"
                        placeholder="#BBA2FE"
                        value={secondaryColor}
                        onChange={(e) => {
                          setSecondaryColor(e.target.value)
                          setApiError(undefined)
                        }}
                        onBlur={() => handleBlur('secondary_color')}
                        error={touched.secondary_color && !!errors.secondary_color}
                        disabled={loading}
                        className="flex-1"
                      />
                    </div>
                  </FormField>
                </div>

                {/* Color Preview */}
                {(primaryColor || secondaryColor) && (
                  <div className="mt-2">
                    <p className="text-sm text-gray-600 mb-2">Color Preview:</p>
                    <div className="flex gap-4 items-center">
                      {primaryColor && (
                        <div className="flex items-center gap-2">
                          <div
                            className="w-8 h-8 rounded-lg border border-gray-200"
                            style={{ backgroundColor: primaryColor }}
                          />
                          <span className="text-sm text-gray-600">Primary</span>
                        </div>
                      )}
                      {secondaryColor && (
                        <div className="flex items-center gap-2">
                          <div
                            className="w-8 h-8 rounded-lg border border-gray-200"
                            style={{ backgroundColor: secondaryColor }}
                          />
                          <span className="text-sm text-gray-600">Secondary</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </Card>

            {/* Portal Configuration Section */}
            <Card>
              <h2 className="font-serif text-xl text-charcoal mb-4">Portal Configuration</h2>

              <div className="space-y-4">
                {/* Landing Page Toggle */}
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-charcoal">Enable Landing Page</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      When enabled, visitors to your portal URL will see a branded landing page with your catalog and a request access form. When disabled, they go directly to the login page.
                    </p>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={landingPageEnabled}
                    onClick={async () => {
                      if (!authPharmacy?.portal_slug) return
                      const newValue = !landingPageEnabled
                      setLandingPageEnabled(newValue)
                      try {
                        await updatePharmacy(authPharmacy.portal_slug, { landing_page_enabled: newValue })
                        await refreshPharmacy()
                        showSuccess(newValue ? 'Landing page enabled' : 'Landing page disabled')
                      } catch (err) {
                        setLandingPageEnabled(!newValue) // revert
                        if (err instanceof ApiClientError) {
                          showError(err.getUserMessage())
                        } else {
                          showError('Failed to update setting')
                        }
                      }
                    }}
                    className={`
                      relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent
                      transition-colors duration-200 ease-in-out
                      focus:outline-none focus-visible:ring-2 focus-visible:ring-lavender focus-visible:ring-offset-2
                      ${landingPageEnabled ? 'bg-deep-purple' : 'bg-gray-300'}
                    `}
                    disabled={loading}
                  >
                    <span
                      className={`
                        pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0
                        transition duration-200 ease-in-out
                        ${landingPageEnabled ? 'translate-x-5' : 'translate-x-0'}
                      `}
                    />
                  </button>
                </div>

                {/* Show Catalog / Show Invite Form sub-toggles (issue #107).
                    Visually nested under "Enable Landing Page" to signal that
                    they only take effect when the landing page is on. The
                    whole group fades out when the parent toggle is off. */}
                <div
                  className={`
                    ml-1 pl-5 border-l-2 border-gray-200 space-y-4
                    transition-opacity duration-200
                    ${landingPageEnabled ? 'opacity-100' : 'opacity-50'}
                  `}
                  aria-disabled={!landingPageEnabled}
                >
                  {/* Show Catalog on Landing Page Toggle */}
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-charcoal">Show Catalog on Landing Page</p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        When enabled, the public catalog appears on your landing page so visitors can browse available products.
                      </p>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={showCatalogOnLanding}
                      onClick={async () => {
                        if (!authPharmacy?.portal_slug) return
                        const newValue = !showCatalogOnLanding
                        setShowCatalogOnLanding(newValue)
                        try {
                          await updatePharmacy(authPharmacy.portal_slug, {
                            show_catalog_on_landing_page: newValue,
                          })
                          await refreshPharmacy()
                          showSuccess(
                            newValue
                              ? 'Catalog will be shown on the landing page'
                              : 'Catalog will be hidden from the landing page'
                          )
                        } catch (err) {
                          setShowCatalogOnLanding(!newValue) // revert
                          if (err instanceof ApiClientError) {
                            showError(err.getUserMessage())
                          } else {
                            showError('Failed to update setting')
                          }
                        }
                      }}
                      className={`
                        relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent
                        transition-colors duration-200 ease-in-out
                        focus:outline-none focus-visible:ring-2 focus-visible:ring-lavender focus-visible:ring-offset-2
                        disabled:cursor-not-allowed
                        ${showCatalogOnLanding ? 'bg-deep-purple' : 'bg-gray-300'}
                      `}
                      disabled={loading || !landingPageEnabled}
                    >
                      <span
                        className={`
                          pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0
                          transition duration-200 ease-in-out
                          ${showCatalogOnLanding ? 'translate-x-5' : 'translate-x-0'}
                        `}
                      />
                    </button>
                  </div>

                  {/* Show Invite Form on Landing Page Toggle */}
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-charcoal">Show Request Access Form</p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        When enabled, visitors can submit an access request from your landing page.
                      </p>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={showInviteFormOnLanding}
                      onClick={async () => {
                        if (!authPharmacy?.portal_slug) return
                        const newValue = !showInviteFormOnLanding
                        setShowInviteFormOnLanding(newValue)
                        try {
                          await updatePharmacy(authPharmacy.portal_slug, {
                            show_invite_form_on_landing_page: newValue,
                          })
                          await refreshPharmacy()
                          showSuccess(
                            newValue
                              ? 'Request access form will be shown on the landing page'
                              : 'Request access form will be hidden from the landing page'
                          )
                        } catch (err) {
                          setShowInviteFormOnLanding(!newValue) // revert
                          if (err instanceof ApiClientError) {
                            showError(err.getUserMessage())
                          } else {
                            showError('Failed to update setting')
                          }
                        }
                      }}
                      className={`
                        relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent
                        transition-colors duration-200 ease-in-out
                        focus:outline-none focus-visible:ring-2 focus-visible:ring-lavender focus-visible:ring-offset-2
                        disabled:cursor-not-allowed
                        ${showInviteFormOnLanding ? 'bg-deep-purple' : 'bg-gray-300'}
                      `}
                      disabled={loading || !landingPageEnabled}
                    >
                      <span
                        className={`
                          pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0
                          transition duration-200 ease-in-out
                          ${showInviteFormOnLanding ? 'translate-x-5' : 'translate-x-0'}
                        `}
                      />
                    </button>
                  </div>
                </div>

                {/* Provider Address Lock Toggle (issues #140 / #180) */}
                <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                  <div>
                    <p className="text-sm font-medium text-charcoal">Manage Provider Addresses</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      When enabled, providers can&apos;t add or edit their own practice addresses — your team manages them from each provider&apos;s detail page instead.
                    </p>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={providerAddressLock}
                    onClick={async () => {
                      if (!authPharmacy?.portal_slug) return
                      const newValue = !providerAddressLock
                      setProviderAddressLock(newValue)
                      try {
                        await updatePharmacy(authPharmacy.portal_slug, {
                          provider_address_lock: newValue,
                        })
                        await refreshPharmacy()
                        showSuccess(
                          newValue
                            ? 'Pharmacy now manages provider addresses'
                            : 'Providers can manage their own addresses'
                        )
                      } catch (err) {
                        setProviderAddressLock(!newValue)
                        if (err instanceof ApiClientError) {
                          showError(err.getUserMessage())
                        } else {
                          showError('Failed to update setting')
                        }
                      }
                    }}
                    className={`
                      relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent
                      transition-colors duration-200 ease-in-out
                      focus:outline-none focus-visible:ring-2 focus-visible:ring-lavender focus-visible:ring-offset-2
                      ${providerAddressLock ? 'bg-deep-purple' : 'bg-gray-300'}
                    `}
                    disabled={loading}
                  >
                    <span
                      className={`
                        pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0
                        transition duration-200 ease-in-out
                        ${providerAddressLock ? 'translate-x-5' : 'translate-x-0'}
                      `}
                    />
                  </button>
                </div>

                {/* Country Field Toggle (issue #190) */}
                <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                  <div>
                    <p className="text-sm font-medium text-charcoal">
                      Add Country field to provider address
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      When enabled, providers can choose a country on their
                      practice address. Non-US selections switch the state
                      field to a free-text input.
                    </p>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={allowCountryField}
                    onClick={async () => {
                      if (!authPharmacy?.portal_slug) return
                      const newValue = !allowCountryField
                      setAllowCountryField(newValue)
                      try {
                        await updatePharmacy(authPharmacy.portal_slug, {
                          allow_country_field: newValue,
                        })
                        await refreshPharmacy()
                        showSuccess(
                          newValue
                            ? 'Country field enabled on provider addresses'
                            : 'Country field hidden on provider addresses'
                        )
                      } catch (err) {
                        setAllowCountryField(!newValue)
                        if (err instanceof ApiClientError) {
                          showError(err.getUserMessage())
                        } else {
                          showError('Failed to update setting')
                        }
                      }
                    }}
                    className={`
                      relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent
                      transition-colors duration-200 ease-in-out
                      focus:outline-none focus-visible:ring-2 focus-visible:ring-lavender focus-visible:ring-offset-2
                      ${allowCountryField ? 'bg-deep-purple' : 'bg-gray-300'}
                    `}
                    disabled={loading}
                  >
                    <span
                      className={`
                        pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0
                        transition duration-200 ease-in-out
                        ${allowCountryField ? 'translate-x-5' : 'translate-x-0'}
                      `}
                    />
                  </button>
                </div>

                {/* Hide Payment & Delivery Section Toggle (PORTAL-65).
                    Master toggle for the whole Payment & Delivery section on the
                    provider prescription form. Its state is the derived
                    `sectionFullyHidden`, so it reads "on" both when the parent
                    flag is set and when an admin hides all three children
                    individually. Turning it ON forces all child toggles ON and
                    locks them; turning it OFF resets all children OFF so each
                    element can be hidden independently again. The backend
                    persists the equivalent state (parent ON + all children ON)
                    via its "all children ON -> parent ON" invariant. */}
                <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                  <div>
                    <p className="text-sm font-medium text-charcoal">Hide Payment &amp; Delivery Section</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      When enabled, the entire Payment &amp; Delivery section (diagnosis code, payment type, and delivery method) is hidden from providers when they create prescriptions.
                    </p>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={sectionFullyHidden}
                    aria-label="Hide Payment & Delivery Section"
                    onClick={async () => {
                      if (!authPharmacy?.portal_slug) return
                      // Reflect the derived state: if the section already reads
                      // as fully hidden (via the parent flag or all three
                      // children), clicking it clears everything; otherwise it
                      // hard-hides the whole section and locks the children.
                      const newValue = !sectionFullyHidden
                      const prev = {
                        section: hidePaymentDeliverySection,
                        diagnosis: hideDiagnosisCode,
                        payment: hidePaymentType,
                        delivery: hideDeliveryMethod,
                      }
                      // Parent ON forces all children ON; parent OFF resets them OFF.
                      setHidePaymentDeliverySection(newValue)
                      setHideDiagnosisCode(newValue)
                      setHidePaymentType(newValue)
                      setHideDeliveryMethod(newValue)
                      try {
                        await updatePharmacy(authPharmacy.portal_slug, {
                          hide_payment_delivery_section: newValue,
                          hide_diagnosis_code: newValue,
                          hide_payment_type: newValue,
                          hide_delivery_method: newValue,
                        })
                        await refreshPharmacy()
                        showSuccess(
                          newValue
                            ? 'Payment & Delivery section will be hidden from providers'
                            : 'Payment & Delivery section will be shown to providers'
                        )
                      } catch (err) {
                        // revert all four
                        setHidePaymentDeliverySection(prev.section)
                        setHideDiagnosisCode(prev.diagnosis)
                        setHidePaymentType(prev.payment)
                        setHideDeliveryMethod(prev.delivery)
                        if (err instanceof ApiClientError) {
                          showError(err.getUserMessage())
                        } else {
                          showError('Failed to update setting')
                        }
                      }
                    }}
                    className={`
                      relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent
                      transition-colors duration-200 ease-in-out
                      focus:outline-none focus-visible:ring-2 focus-visible:ring-lavender focus-visible:ring-offset-2
                      ${sectionFullyHidden ? 'bg-deep-purple' : 'bg-gray-300'}
                    `}
                    disabled={loading}
                  >
                    <span
                      className={`
                        pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0
                        transition duration-200 ease-in-out
                        ${sectionFullyHidden ? 'translate-x-5' : 'translate-x-0'}
                      `}
                    />
                  </button>
                </div>

                {/* Child toggles: hide individual Payment & Delivery elements.
                    Locked (greyed) whenever the section is fully hidden — either
                    the parent flag is ON or all three are hidden individually.
                    Once locked, the only way to edit a single element is to turn
                    the master toggle OFF, which resets all three to visible.
                    The "Required to submit" checkboxes (PORTAL-202) stay
                    editable while greyed, so the container is not
                    aria-disabled; the hide switches carry their own native
                    `disabled`. */}
                <div
                  className={`
                    ml-1 pl-5 border-l-2 border-gray-200 space-y-4
                    transition-opacity duration-200
                    ${sectionFullyHidden ? 'opacity-50' : 'opacity-100'}
                  `}
                >
                  {/* Hide Diagnosis Code (ICD-10) Toggle */}
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-charcoal">Hide Diagnosis Code (ICD-10)</p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        When enabled, the diagnosis code (ICD-10) field is hidden from the prescription form.
                      </p>
                      {renderRequiredCheckbox(
                        'diagnosis_code_required',
                        'Diagnosis code',
                        diagnosisCodeRequired,
                        setDiagnosisCodeRequired,
                        diagnosisCodeHidden,
                      )}
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={hideDiagnosisCode}
                      aria-label="Hide Diagnosis Code (ICD-10)"
                      onClick={async () => {
                        if (!authPharmacy?.portal_slug) return
                        const newValue = !hideDiagnosisCode
                        setHideDiagnosisCode(newValue)
                        try {
                          await updatePharmacy(authPharmacy.portal_slug, {
                            hide_diagnosis_code: newValue,
                          })
                          await refreshPharmacy()
                          showSuccess(
                            newValue
                              ? 'Diagnosis code will be hidden from providers'
                              : 'Diagnosis code will be shown to providers'
                          )
                        } catch (err) {
                          setHideDiagnosisCode(!newValue) // revert
                          if (err instanceof ApiClientError) {
                            showError(err.getUserMessage())
                          } else {
                            showError('Failed to update setting')
                          }
                        }
                      }}
                      className={`
                        relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent
                        transition-colors duration-200 ease-in-out
                        focus:outline-none focus-visible:ring-2 focus-visible:ring-lavender focus-visible:ring-offset-2
                        disabled:cursor-not-allowed
                        ${hideDiagnosisCode ? 'bg-deep-purple' : 'bg-gray-300'}
                      `}
                      disabled={loading || sectionFullyHidden}
                    >
                      <span
                        className={`
                          pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0
                          transition duration-200 ease-in-out
                          ${hideDiagnosisCode ? 'translate-x-5' : 'translate-x-0'}
                        `}
                      />
                    </button>
                  </div>

                  {/* Hide Payment Type Toggle */}
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-charcoal">Hide Payment Type</p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        When enabled, the payment type selection is hidden from the prescription form.
                      </p>
                      {renderRequiredCheckbox(
                        'payment_type_required',
                        'Payment type',
                        paymentTypeRequired,
                        setPaymentTypeRequired,
                        paymentTypeHidden,
                      )}
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={hidePaymentType}
                      aria-label="Hide Payment Type"
                      onClick={async () => {
                        if (!authPharmacy?.portal_slug) return
                        const newValue = !hidePaymentType
                        setHidePaymentType(newValue)
                        try {
                          await updatePharmacy(authPharmacy.portal_slug, {
                            hide_payment_type: newValue,
                          })
                          await refreshPharmacy()
                          showSuccess(
                            newValue
                              ? 'Payment type will be hidden from providers'
                              : 'Payment type will be shown to providers'
                          )
                        } catch (err) {
                          setHidePaymentType(!newValue) // revert
                          if (err instanceof ApiClientError) {
                            showError(err.getUserMessage())
                          } else {
                            showError('Failed to update setting')
                          }
                        }
                      }}
                      className={`
                        relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent
                        transition-colors duration-200 ease-in-out
                        focus:outline-none focus-visible:ring-2 focus-visible:ring-lavender focus-visible:ring-offset-2
                        disabled:cursor-not-allowed
                        ${hidePaymentType ? 'bg-deep-purple' : 'bg-gray-300'}
                      `}
                      disabled={loading || sectionFullyHidden}
                    >
                      <span
                        className={`
                          pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0
                          transition duration-200 ease-in-out
                          ${hidePaymentType ? 'translate-x-5' : 'translate-x-0'}
                        `}
                      />
                    </button>
                  </div>

                  {/* Hide Delivery Method Toggle */}
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-charcoal">Hide Delivery Method</p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        When enabled, the delivery method selection is hidden from the prescription form.
                      </p>
                      {renderRequiredCheckbox(
                        'delivery_method_required',
                        'Delivery method',
                        deliveryMethodRequired,
                        setDeliveryMethodRequired,
                        deliveryMethodHidden,
                      )}
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={hideDeliveryMethod}
                      aria-label="Hide Delivery Method"
                      onClick={async () => {
                        if (!authPharmacy?.portal_slug) return
                        const newValue = !hideDeliveryMethod
                        setHideDeliveryMethod(newValue)
                        try {
                          await updatePharmacy(authPharmacy.portal_slug, {
                            hide_delivery_method: newValue,
                          })
                          await refreshPharmacy()
                          showSuccess(
                            newValue
                              ? 'Delivery method will be hidden from providers'
                              : 'Delivery method will be shown to providers'
                          )
                        } catch (err) {
                          setHideDeliveryMethod(!newValue) // revert
                          if (err instanceof ApiClientError) {
                            showError(err.getUserMessage())
                          } else {
                            showError('Failed to update setting')
                          }
                        }
                      }}
                      className={`
                        relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent
                        transition-colors duration-200 ease-in-out
                        focus:outline-none focus-visible:ring-2 focus-visible:ring-lavender focus-visible:ring-offset-2
                        disabled:cursor-not-allowed
                        ${hideDeliveryMethod ? 'bg-deep-purple' : 'bg-gray-300'}
                      `}
                      disabled={loading || sectionFullyHidden}
                    >
                      <span
                        className={`
                          pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0
                          transition duration-200 ease-in-out
                          ${hideDeliveryMethod ? 'translate-x-5' : 'translate-x-0'}
                        `}
                      />
                    </button>
                  </div>
                </div>

                {/* Signature Required Toggle */}
                <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                  <div>
                    <p className="text-sm font-medium text-charcoal">Require Provider Signature</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      When enabled, providers must electronically sign each prescription before submitting. When disabled, the signature pad is still shown but signing is optional. Any signature will appear on the fax.
                    </p>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={signatureRequired}
                    onClick={async () => {
                      if (!authPharmacy?.portal_slug) return
                      const newValue = !signatureRequired
                      setSignatureRequired(newValue)
                      try {
                        await updatePharmacy(authPharmacy.portal_slug, { signature_required: newValue })
                        await refreshPharmacy()
                        showSuccess(newValue ? 'Signature requirement enabled' : 'Signature requirement disabled')
                      } catch (err) {
                        setSignatureRequired(!newValue) // revert
                        if (err instanceof ApiClientError) {
                          showError(err.getUserMessage())
                        } else {
                          showError('Failed to update setting')
                        }
                      }
                    }}
                    className={`
                      relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent
                      transition-colors duration-200 ease-in-out
                      focus:outline-none focus-visible:ring-2 focus-visible:ring-lavender focus-visible:ring-offset-2
                      ${signatureRequired ? 'bg-deep-purple' : 'bg-gray-300'}
                    `}
                    disabled={loading}
                  >
                    <span
                      className={`
                        pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0
                        transition duration-200 ease-in-out
                        ${signatureRequired ? 'translate-x-5' : 'translate-x-0'}
                      `}
                    />
                  </button>
                </div>

                {/* Patient Address Required Toggle (PORTAL-72) */}
                <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                  <div>
                    <p className="text-sm font-medium text-charcoal">Require Patient Address</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      When enabled, providers must enter a patient address to submit a prescription for a human patient. Can be overridden per provider.
                    </p>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={patientAddressRequired}
                    onClick={async () => {
                      if (!authPharmacy?.portal_slug) return
                      const newValue = !patientAddressRequired
                      setPatientAddressRequired(newValue)
                      try {
                        await updatePharmacy(authPharmacy.portal_slug, { patient_address_required: newValue })
                        await refreshPharmacy()
                        showSuccess(newValue ? 'Patient address requirement enabled' : 'Patient address requirement disabled')
                      } catch (err) {
                        setPatientAddressRequired(!newValue) // revert
                        if (err instanceof ApiClientError) {
                          showError(err.getUserMessage())
                        } else {
                          showError('Failed to update setting')
                        }
                      }
                    }}
                    className={`
                      relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent
                      transition-colors duration-200 ease-in-out
                      focus:outline-none focus-visible:ring-2 focus-visible:ring-lavender focus-visible:ring-offset-2
                      ${patientAddressRequired ? 'bg-deep-purple' : 'bg-gray-300'}
                    `}
                    disabled={loading}
                  >
                    <span
                      className={`
                        pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0
                        transition duration-200 ease-in-out
                        ${patientAddressRequired ? 'translate-x-5' : 'translate-x-0'}
                      `}
                    />
                  </button>
                </div>

                {/* Phone Required Toggle (PORTAL-163) */}
                <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                  <div>
                    <p className="text-sm font-medium text-charcoal">Phone mandatory for provider</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      When enabled, providers must enter a phone number to complete their profile. Can be overridden per provider.
                    </p>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={phoneRequired}
                    onClick={async () => {
                      if (!authPharmacy?.portal_slug) return
                      const newValue = !phoneRequired
                      setPhoneRequired(newValue)
                      try {
                        await updatePharmacy(authPharmacy.portal_slug, { phone_required: newValue })
                        await refreshPharmacy()
                        showSuccess(newValue ? 'Provider phone requirement enabled' : 'Provider phone requirement disabled')
                      } catch (err) {
                        setPhoneRequired(!newValue) // revert
                        if (err instanceof ApiClientError) {
                          showError(err.getUserMessage())
                        } else {
                          showError('Failed to update setting')
                        }
                      }
                    }}
                    className={`
                      relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent
                      transition-colors duration-200 ease-in-out
                      focus:outline-none focus-visible:ring-2 focus-visible:ring-lavender focus-visible:ring-offset-2
                      ${phoneRequired ? 'bg-deep-purple' : 'bg-gray-300'}
                    `}
                    disabled={loading}
                  >
                    <span
                      className={`
                        pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0
                        transition duration-200 ease-in-out
                        ${phoneRequired ? 'translate-x-5' : 'translate-x-0'}
                      `}
                    />
                  </button>
                </div>

                {/* Hide Machine-Readable Zone Toggle (PORTAL-71) */}
                <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                  <div>
                    <p className="text-sm font-medium text-charcoal">Hide Machine-Readable Zone on Faxes</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      When enabled, new prescription faxes are sent without the Machine-Readable Zone, making them shorter. Leave this off if your faxes are processed automatically.
                    </p>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={hideMachineReadableZone}
                    aria-label="Hide Machine-Readable Zone on Faxes"
                    onClick={async () => {
                      if (!authPharmacy?.portal_slug) return
                      const newValue = !hideMachineReadableZone
                      setHideMachineReadableZone(newValue)
                      try {
                        await updatePharmacy(authPharmacy.portal_slug, { hide_machine_readable_zone: newValue })
                        await refreshPharmacy()
                        showSuccess(newValue ? 'Machine-Readable Zone hidden on new faxes' : 'Machine-Readable Zone shown on new faxes')
                      } catch (err) {
                        setHideMachineReadableZone(!newValue) // revert
                        if (err instanceof ApiClientError) {
                          showError(err.getUserMessage())
                        } else {
                          showError('Failed to update setting')
                        }
                      }
                    }}
                    className={`
                      relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent
                      transition-colors duration-200 ease-in-out
                      focus:outline-none focus-visible:ring-2 focus-visible:ring-lavender focus-visible:ring-offset-2
                      ${hideMachineReadableZone ? 'bg-deep-purple' : 'bg-gray-300'}
                    `}
                    disabled={loading}
                  >
                    <span
                      className={`
                        pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0
                        transition duration-200 ease-in-out
                        ${hideMachineReadableZone ? 'translate-x-5' : 'translate-x-0'}
                      `}
                    />
                  </button>
                </div>

                {/* Prescribing Mode */}
                <div className="pt-4 border-t border-gray-200">
                  <p className="text-sm font-medium text-charcoal">Prescribing Mode</p>
                  <p className="text-xs text-gray-500 mt-0.5 mb-3">
                    Choose which types of patients your providers can prescribe for.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    {([
                      { value: 'human' as PortalType, label: 'Human Only', desc: 'Only human patients' },
                      { value: 'veterinary' as PortalType, label: 'Veterinary Only', desc: 'Only animal patients' },
                      { value: 'both' as PortalType, label: 'Both', desc: 'Human and animal patients' },
                    ]).map((option) => (
                      <label
                        key={option.value}
                        className={`
                          flex-1 flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors
                          ${portalType === option.value
                            ? 'border-deep-purple bg-soft-lavender/30'
                            : 'border-gray-200 hover:border-gray-300'
                          }
                          ${loading ? 'opacity-50 pointer-events-none' : ''}
                        `}
                      >
                        <input
                          type="radio"
                          name="portal_type"
                          value={option.value}
                          checked={portalType === option.value}
                          onChange={() => setPortalType(option.value)}
                          disabled={loading}
                          className="mt-0.5 accent-deep-purple"
                        />
                        <div>
                          <span className="text-sm font-medium text-charcoal">{option.label}</span>
                          <p className="text-xs text-gray-500">{option.desc}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </Card>

            {/* Pharmacy Notes Section (PORTAL-159) */}
            <Card>
              <h2 className="font-serif text-xl text-charcoal mb-4">Pharmacy Notes</h2>

              <div className="space-y-4">
                <p className="text-sm text-gray-600">
                  Publish rules, regulations, or any other notes to your providers.
                  Everyone at your pharmacy&apos;s portal sees the same note on their{' '}
                  <strong>Pharmacy Notes</strong> tab. Leave it empty to hide that
                  tab entirely.
                </p>

                <PharmacyNotesEditor
                  value={pharmacyNotes}
                  onChange={(value) => {
                    setPharmacyNotes(value)
                    setApiError(undefined)
                  }}
                  disabled={loading}
                />
              </div>
            </Card>

            {/* Portal Link Section */}
            <Card>
              <h2 className="font-serif text-xl text-charcoal mb-4">Portal Link</h2>

              <div className="space-y-4">
                <p className="text-sm text-gray-600">
                  Share this link with your providers to access the prescription portal.
                </p>

                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="flex-1 bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
                    <code className="text-sm text-charcoal break-all">{portalUrl}</code>
                  </div>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={copyPortalUrl}
                    className="sm:w-auto whitespace-nowrap"
                  >
                    {copySuccess ? (
                      <>
                        <svg
                          className="w-4 h-4 mr-2"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                        Copied!
                      </>
                    ) : (
                      <>
                        <svg
                          className="w-4 h-4 mr-2"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                          />
                        </svg>
                        Copy Link
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </Card>

            {/* Save Button */}
            <div className="flex justify-end">
              <Button
                type="submit"
                variant="primary"
                size="lg"
                loading={loading}
                disabled={loading || hasValidationErrors}
              >
                Save Settings
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}

export default Settings
